import {
  AcceptResultCode,
  type AcceptDispatchRequest,
  type AcceptResponse,
  type AcceptOpaqueData,
  type AcceptCardData,
  type AcceptOkResponse,
  type AcceptErrorResponse,
  type AcceptBankData,
} from '~/types/accept';

/**
 * Function signature for the Accept.js dispatchData method.
 *
 * @param request - The tokenization request (card or bank data + authData).
 * @param callback - Callback invoked with either an Ok or Error response.
 */
type AcceptDispatchFn = (
  request: AcceptDispatchRequest,
  callback: (response: AcceptResponse) => void
) => void;

/**
 * Global window type augmentation to include Accept.js.
 */
declare global {
  interface Window {
    Accept: {
      /** Sends payment data to Authorize.Net for tokenization. */
      dispatchData: AcceptDispatchFn;
    };
  }
}

export default defineNuxtPlugin({
  name: 'kygunco:accept',
  dependsOn: [],
  parallel: true,
  setup: () => {
    const runtimeConfig = useRuntimeConfig().public.accept;

    const scriptUrl = runtimeConfig.sandbox
      ? 'https://jstest.authorize.net/v1/Accept.js'
      : 'https://js.authorize.net/v1/Accept.js';

    const { status, load } = useScript(
      {
        src: scriptUrl,
      },
      {
        trigger: 'manual',
        mode: 'client',
        use: () => window.Accept,
      });

    /**
     * Reactive flag indicating whether Accept.js has been loaded successfully.
     *
     * `true` ⇒ the Accept.js script finished loading and is ready to dispatch tokenization requests.
     */
    const ready = computed(() => status.value == 'loaded');

    /**
     * Internal helper to call Accept.dispatchData and return a Promise of `opaqueData`.
     * Throws with a readable message on Authorize.Net errors.
     */
    const dispatchData = async (request: AcceptDispatchRequest) => {
      await load();

      return new Promise<AcceptOpaqueData>((resolve, reject) => {
        if (!window.Accept) {
          return reject(new Error('Accept.js is not available. Please try again later.'));
        }

        window.Accept.dispatchData(request, (res: AcceptResponse) => {
          console.log('Received response from Accept.js:', res);

          if (res.messages.resultCode === AcceptResultCode.Ok) {
            const response = res as Extract<AcceptResponse, AcceptOkResponse>;
            resolve(response.opaqueData);
          }
          else {
            const response = res as Extract<AcceptResponse, AcceptErrorResponse>;
            const msg = response.messages.message.map(e => `${e.code}: ${e.text}`).join(' / ')
              || 'Unknown error occurred';
            reject(new Error(msg));
          }
        });
      });
    };

    /**
     * Tokenize raw card data using Accept.js and return `opaqueData`.
     *
     * ⚠️ PCI note:
     * Passing raw card details from your own inputs increases PCI scope (typically SAQ A‑EP).
     * For lowest scope (SAQ A), use hosted fields so your app never handles PAN/CVV directly,
     * and call `dispatchData` with those hosted-field references instead.
     *
     * @param cardData - Card details (PAN, expiry, optional CVV/ZIP/name).
     * @returns Promise resolving to `opaqueData` (descriptor + value) for server-side authorization.
     */
    const tokenizeCard = async (cardData: AcceptCardData) => {
      const request: AcceptDispatchRequest = {
        authData: {
          clientKey: runtimeConfig.clientKey,
          apiLoginID: runtimeConfig.apiLoginId,
        },
        cardData,
      };

      return dispatchData(request);
    };

    /**
     * Tokenize bank (eCheck.Net) data using Accept.js and return `opaqueData`.
     *
     * @param bankData - Bank account details (routing, account, holder, type).
     * @returns Promise resolving to `opaqueData` for server-side authorization.
     */
    const tokenizeBank = async (bankData: AcceptBankData) => {
      const request: AcceptDispatchRequest = {
        authData: {
          clientKey: runtimeConfig.clientKey,
          apiLoginID: runtimeConfig.apiLoginId,
        },
        bankData,
      };

      return dispatchData(request);
    };

    return {
      provide: {
        accept: {
          load,
          ready,
          tokenizeCard,
          tokenizeBank,
        },
      },
    };
  },
});
